import React, { useEffect, useState, useRef, forwardRef } from "react";
import ProfileSection from "./components/ProfileSection";
import LedgerSearchDropdown from "./components/LedgerSearchDropdown";
import { headerConfig } from "./headerConfig";
// @ts-ignore: allow side-effect CSS import without module declarations
import "../../../business-types/laundry/modules/home/Home.css";
import { useLedgerSearch } from "./hooks/useLedgerSearch";
import { useUser } from "src/shared/context/UserContext";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import Image from "src/shared/Image";
import { useLocation } from "react-router-dom";
import { COMPANY_NAME } from "src/shared/utils/brand";

interface HeaderProps {
  Header: string;
  pageTitle?: string;
}

const Header = forwardRef<HTMLDivElement, HeaderProps>(({ Header, pageTitle }, ref) => {
  const [profileName, setProfileName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const [, setOrganizationName] = useState("");
  const [username, setUsername] = useState("");
  const [logoVersion, setLogoVersion] = useState(Date.now());

  const searchContainerRef = useRef<HTMLDivElement>(null);

  const { searchTerm, results, loading, setSearchTerm } = useLedgerSearch();

  const { user, userConfigration } = useUser();
  const location = useLocation();

  const { fontClass, bgClass, borderHighlight } = useUserTheme();

  useEffect(() => {
    if (user?.businessLogo) {
      setLogoVersion(Date.now());
    }
  }, [user?.businessLogo]);

  useEffect(() => {
    const storedUsername = sessionStorage.getItem("username") || "";
    const storedProfileName = sessionStorage.getItem("Profilename") || "";

    setProfileName(user?.firstName || storedProfileName);
    setBusinessType(user?.businessType || "");
    setUsername(user?.userName || storedUsername);

    setOrganizationName(sessionStorage.getItem("organization_name") || "Organization Name");
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchClicked(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Header configuration
  const config = headerConfig[businessType] || {
    showSearch: true,
    gridClass: "grid-container",
    titleClass: "text-xl font-bold",
    extraRight: null,
  };

  const formatted = location.pathname
    .split("/")
    .filter(Boolean)
    .filter((s) => s.length < 20)
    .slice(0, 3)
    .map((s) => {
      if (s.toLowerCase() === "grocery") return userConfigration.customBusinessName;
      if (s.toLowerCase().match(/^v\d+$/)) return null;
      return s.charAt(0).toUpperCase() + s.slice(1);
    })
    .filter(Boolean)
    .join(" - ");

  const header = location.pathname
    .split("/")
    .filter(Boolean)
    .filter((s) => s.length < 20)
    .reverse()
    .map((s) => {
      if (s.toLowerCase().match(/^v\d+$/)) return null;
      return s.charAt(0).toUpperCase() + s.slice(1);
    })
    .filter(Boolean)
    .join(" - ");

  useEffect(() => {
    document.title = `${pageTitle ? `${pageTitle} - ` : ""}${header} - ${COMPANY_NAME}`;
  }, [header, pageTitle]);

  useEffect(() => {
    setSearchTerm("");
    setSearchClicked(false);
  }, [location.pathname]);

  return (
    <header className={`head-name ${bgClass} ${fontClass}`} id="head_name" ref={ref}>
      {/* Logo + Organization */}
      <div className="ml-14">
        <div className="brand-flex">
          <div className="logo-header">
            <Image imagePath={user?.businessLogo ? `${user.businessLogo}?v=${logoVersion}` : ""} className="logo-container" company={true} />{" "}
          </div>
          <h1 className="text-lg font-semibold sm:text-xl">{user?.orgName ? (user.orgName.length > 8 ? `${user.orgName.slice(0, 8)}...` : user.orgName) : ""}</h1>
          <h1 className="text-xl font-medium">- {formatted}</h1>
        </div>
      </div>

      {/* Right section (search, restaurant orders, profile) */}
      <div
        className={config.gridClass}
        style={{
          width: businessType === "Restaurant" ? "640px" : "500px",
          display: "flex",
          justifyContent: "space-evenly",
          alignItems: "center",
        }}
      >
        {/* ✅ Hide ledger search if Restaurant */}
        {businessType !== "Restaurant" && config.showSearch && (
          <div className="search_header relative w-full" ref={searchContainerRef}>
            <input
              name="Ledger search"
              type="text"
              autoComplete="off"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSearchClicked(true);
              }}
              onFocus={() => setSearchClicked(true)}
              placeholder="Ledger search by Name or Phone number or Client Id"
              className={`w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${borderHighlight} ${fontClass} text-sm text-black`}
            />

            {searchClicked && searchTerm.trim().length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 bg-white rounded-b-md shadow-lg border border-t-0 border-gray-200 overflow-hidden animate-[fadeIn_0.2s_ease-in-out]">
                {/* Header with search info */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">LEDGER RESULTS</span>
                    <span className="text-xs text-gray-400">{results.length} results found</span>
                  </div>
                </div>

                {/* Results - Responsive */}
                <div className="max-h-[min(400px,50vh)] overflow-y-auto custom-scrollbar">
                  {loading ? (
                    <div className="flex items-center justify-center gap-3 p-8">
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-600">Searching...</span>
                    </div>
                  ) : results.length > 0 ? (
                      // results may come from an older interface version; cast to any to avoid type incompatibility
                      <LedgerSearchDropdown results={results as any} onClose={() => setSearchClicked(false)} />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">No results found</p>
                        <p className="text-xs text-gray-400 mt-1">Try adjusting your search</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile */}
        <div
          className="flex"
          style={{
            marginLeft: "auto",
            marginRight: "1rem",
          }}
        >
          <ProfileSection profileName={profileName} username={username} />
        </div>
      </div>
    </header>
  );
});

export default Header;
